<script lang="ts" setup>
import userStore from '@/store/UserStore'
import { ButtonSetProps, CoolingCalculationMethod, EnergySource, FormCircuit, HeatingCalculationMethod } from '@/helper/Types'
import i18n, { formatLanguage } from '@/helper/Translations'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import formStore from '@/store/FormStore'
import { computed, onMounted, reactive, ref } from 'vue'
import HeatingSystemSection from '@/components/steps/planning/HeatingSystemSection.vue'
import CalculateHeatingLoadSection from '@/components/steps/planning/HeatingLoadCalculationSection.vue'
import CalculateCoolingLoadSection from '@/components/steps/planning/CoolingLoadCalculationSection.vue'
import HeatPumpPlacementSection from '@/components/steps/planning/HeatPumpPlacementSection.vue'
import EnergySourceSection from '@/components/steps/planning/EnergySourceSection.vue'
import DomesticHotWaterSection from '@/components/steps/planning/DomesticHotWaterSection.vue'
import BuildingDataSection from '@/components/steps/planning/BuildingDataSection.vue'
import ConsumptionSection from '@/components/steps/planning/ConsumptionSection.vue'
import CalculationSection from '@/components/steps/planning/CalculationSection.vue'
import InstallationTypeSection from '@/components/steps/planning/InstallationTypeSection.vue'
import GroundTypeSection from '@/components/steps/planning/GroundTypeSection.vue'
import ProjectController from '@/controllers/ProjectController'
import { debounce } from '@/helper/tools'
import { StepsController } from '@/controllers/StepsController'
import { BuildingStory } from '@/models/BuildingStory.ts'

const validator: ValidatorType = reactive({})
const isCircuitsInvalid = ref(false)
const areStoriesValid = ref(false)
const calculatedHeatingLoad = ref('')
const calculatedCoolingLoad = ref('')

onMounted(() => {
  if (envelopeMethodIsActive.value || calculateCoolingLoadIsActive.value) {
    updateLoad()
  }
})

const energySourcesButtonProps = computed(() => {
  const buttons: ButtonSetProps[] = []
  for (const energySource of userStore.energySources) {
    buttons.push({
      caption: i18n.global.t(`step_heating_details.source_${energySource.name}`).toString(),
      info: undefined,
      value: energySource.name.toString(),
      iconIdentifier: `ic-${energySource.name.toString().split('_')[0]}`,
      iconUrl: undefined,
      disabled: () => energySourceDisabled(energySource.name as EnergySource)
    })
  }
  return buttons
})

const buildingDataIsActive = computed(() => envelopeMethodIsActive.value || calculateCoolingLoadIsActive.value)

const envelopeMethodIsActive = computed(
  () => formStore.isHeating && formStore.data.heatingCalculationMethod === HeatingCalculationMethod.THERMAL_ENVELOPE
)

const calculateCoolingLoadIsActive = computed(
  () => formStore.isCooling && formStore.data.coolingCalculationMethod === CoolingCalculationMethod.THERMAL_ENVELOPE
)

function setCalculateHeatingLoad(calculateLoad: boolean) {
  let method = HeatingCalculationMethod.MANUAL

  if (calculateLoad) {
    if (userStore.enableHeatingThermalEnvelope) {
      method = HeatingCalculationMethod.THERMAL_ENVELOPE
    } else if (userStore.enableHeatingConsumption) {
      method = HeatingCalculationMethod.CONSUMPTION
    }
  }

  formStore.data.heatingCalculationMethod = method
}

function setCalculateCoolingLoad(calculateLoad: boolean) {
  formStore.data.coolingCalculationMethod = calculateLoad
    ? CoolingCalculationMethod.THERMAL_ENVELOPE
    : CoolingCalculationMethod.MANUAL
}

function updateLoad(): void {
  updateHeatingLoad()
  updateCoolingLoad()
}

const updateCoolingLoad = debounce(() => {
  ProjectController.calculateCoolingLoad()
    .then((coolingLoad: number | null) => {
      calculatedCoolingLoad.value = coolingLoad?.toLocaleString(formatLanguage(), { maximumFractionDigits: 2 }) ?? ''
    })
    .catch(e => console.error('Error calculating cooling load via API:', e))
}, 500)

const updateHeatingLoad = debounce(() => {
  ProjectController.calculateHeatingLoad()
    .then((heatingLoad: number | null) => {
      calculatedHeatingLoad.value = heatingLoad?.toLocaleString(formatLanguage(), { maximumFractionDigits: 2 }) ?? ''
    })
    .catch(e => console.error('Error calculating heating load via API:', e))
}, 500)

function validateCircuits(force = false): boolean {
  if (!force && !isCircuitsInvalid.value) {
    return true
  }
  if (formStore.isCooling) {
    isCircuitsInvalid.value = !(formStore.data.circuits as FormCircuit[]).some(circuit => circuit.cooling)
  } else {
    isCircuitsInvalid.value = false
  }
  return !isCircuitsInvalid.value
}

function energySourceDisabled(energySource: EnergySource): boolean {
  if (formStore.isCooling && energySource !== EnergySource.AIR) {
    return true
  }

  const configuredEnergySource = userStore.energySources.find(e => e.name === energySource)

  if (configuredEnergySource?.maxFlowTemperature === undefined) {
    return false
  }

  return (formStore.data.circuits as FormCircuit[])
    .filter(c => c.flowTemperature !== undefined)
    .some(c => (c.flowTemperature ?? 0) > configuredEnergySource.maxFlowTemperature)
}

function flowTempChanged(): void {
  if (energySourceDisabled(formStore.data.heatSource as EnergySource)) {
    formStore.data.heatSource = EnergySource.NONE
  }
}

function validateBuildingStories() {
  if (!buildingDataIsActive.value) {
    return true
  }

  if (formStore.data.buildingHull) {
    const onlyOneTopStory = formStore.data.buildingHull.stories.filter((s: BuildingStory) => s.topStory).length === 1
    const onlyOneBottomStory = formStore.data.buildingHull.stories.filter((s: BuildingStory) => s.bottomStory).length === 1
    const onlyOneTopAndBottomFloor = formStore.data.buildingHull.stories.filter((s: BuildingStory) => s.bottomStory && s.topStory).length === 1

    if (formStore.data.buildingHull.stories.length > 1 && onlyOneTopAndBottomFloor) {
      areStoriesValid.value = false
    } else {
      areStoriesValid.value = onlyOneTopStory && onlyOneBottomStory
    }
  } else {
    areStoriesValid.value = false
  }

  return areStoriesValid.value
}

function validate() {
  return validateComponent(validator) && validateBuildingStories() && validateCircuits(true)
}

defineExpose({
  validate
})
</script>

<template>
  <div class="steps__content" data-hj-whitelist>
    <form
      id="planning-form"
      action=""
      class="form steps__form card"
      @submit.prevent="StepsController.nextClicked()"
    >
      <InstallationTypeSection
        :energy-sources-button-props="energySourcesButtonProps"
        :validator="validator"
        @validate-circuits="validateCircuits"
      />

      <CalculationSection
        :validator="validator"
        @toggle-cooling-load-calc="calculateLoad => setCalculateCoolingLoad(calculateLoad)"
        @toggle-heating-load-calc="calculateLoad => setCalculateHeatingLoad(calculateLoad)"
      />

      <ConsumptionSection
        v-if="formStore.data.heatingCalculationMethod === HeatingCalculationMethod.CONSUMPTION"
        :validator="validator"
      />

      <BuildingDataSection
        v-if="buildingDataIsActive"
        :are-stories-valid="areStoriesValid"
        :validator="validator"
        @validate-building-stories="validateBuildingStories"
        @update-load="updateLoad"
      />

      <CalculateHeatingLoadSection
        v-if="envelopeMethodIsActive"
        :calculated-heating-load="calculatedHeatingLoad"
        :validator="validator"
        @update-heating-load="updateHeatingLoad"
      />

      <CalculateCoolingLoadSection
        v-if="calculateCoolingLoadIsActive"
        :calculated-cooling-load="calculatedCoolingLoad"
        :validator="validator"
        @update-cooling-load="updateCoolingLoad"
      />

      <DomesticHotWaterSection :validator="validator" />

      <HeatingSystemSection
        :is-circuits-invalid="isCircuitsInvalid"
        :validator="validator"
        @validate-circuits="validateCircuits"
        @flow-temp-changed="flowTempChanged"
      />

      <EnergySourceSection :energy-sources-button-props="energySourcesButtonProps" :validator="validator" />

      <GroundTypeSection
        v-if="userStore.groundTypes && formStore.data.heatSource === EnergySource.GROUND_VERTICAL"
        :validator="validator"
      />

      <HeatPumpPlacementSection
        v-if="userStore.heatPumpPlacement && formStore.data.heatSource === EnergySource.AIR"
        :validator="validator"
      />

      <input class="is--invisible" type="submit">
    </form>
  </div>
</template>
